import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";
import { AltHintTarget } from "../../components/AltHintTarget/AltHintTarget.jsx";
import { ControlBar } from "../../components/ControlBar/ControlBar.jsx";
import { ProgressMeter } from "../../components/ProgressMeter/ProgressMeter.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { SegmentedControl } from "../../components/SegmentedControl/SegmentedControl.jsx";
import { formatCurrency } from "../../lib/formatters.js";
import { createSectionNavigationBindings } from "../../lib/shortcuts/createSectionNavigationBindings.js";
import { onKey } from "../../lib/shortcuts/onKey.js";
import {
  setAnalysisChartCategory,
  setAnalysisChartSupplier,
  setBudgetSearchQuery,
  setExpensesSearchQuery
} from "../../lib/pageSearchState.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import { handlePageEscapeReset, hasPageEscapeResetAction } from "../../lib/shortcuts/pageEscapeReset.js";
import { useEscapeStack } from "../../lib/shortcuts/useEscapeStack.js";
import { usePageStateMemory } from "../../lib/usePageStateMemory.js";
import { useRememberedSortState } from "../../lib/useRememberedSortState.js";
import { budgetUsagePercent } from "../../lib/finance/budgetMath.js";
import { calculateMonthlyImpliedSpend } from "../../lib/finance/projectionMath.js";
import { HierarchicalReviewSurface } from "./HierarchicalReviewSurface.jsx";
import {
  ANALYSIS_BUDGET_REVIEW_FILTER_ALL,
  ANALYSIS_BUDGET_REVIEW_FILTER_OVER,
  ANALYSIS_BUDGET_REVIEW_FILTER_WITHIN
} from "../../lib/settings/profileSettings.js";
import "./BudgetReview.css";

const BUDGET_REVIEW_EXPANDED_SESSION_KEY = "wallet-watch:budget-review-expanded";
const BUDGET_REVIEW_MODE_SESSION_KEY = "wallet-watch:budget-review-mode";
const BUDGET_REVIEW_SELECTION_SESSION_KEY = "wallet-watch:budget-review-selection";
const BUDGET_REVIEW_SORT_SESSION_KEY = "wallet-watch:budget-review-sort";
const BUDGET_REVIEW_SORT_LABEL = "label";
const BUDGET_REVIEW_SORT_BUDGET = "budget";
const BUDGET_REVIEW_SORT_IMPLIED = "implied";
const BUDGET_REVIEW_SORT_VARIANCE = "variance";
const BUDGET_REVIEW_SORT_PERCENT = "percent";
const BUDGET_REVIEW_SORT_METER = "meter";
const BUDGET_REVIEW_MODE_ONGOING = "ongoing";
const BUDGET_REVIEW_MODE_IRREGULAR = "irregular";

function normalizeExpenseType(value) {
  return String(value ?? "").trim().toLocaleLowerCase();
}

function normalizeBudgetReviewKeyPart(value, fallback = "") {
  const normalized = String(value ?? "").trim().toLocaleLowerCase();
  return normalized || fallback;
}

function categoryExpenseRows(expenseRows, categoryName) {
  return expenseRows.filter((expenseRow) => String(expenseRow.category ?? "").trim() === categoryName);
}

function getSupplierLabel(expenseRow, t) {
  return String(expenseRow.supplier ?? "").trim() || t("unlabeled");
}

function sumExpenseAmounts(expenseRows) {
  return expenseRows.reduce((sum, expenseRow) => sum + Number(expenseRow.amount ?? 0), 0);
}

function getBudgetReviewCategoryValues(row, categoryExpenses, dateInput, reviewMode) {
  if (reviewMode === BUDGET_REVIEW_MODE_IRREGULAR) {
    const budget = Number(row.irregularBudget ?? 0);
    const spent = sumExpenseAmounts(categoryExpenses);

    return {
      budget,
      actual: spent,
      variance: spent - budget
    };
  }

  const budget = Number(row.ongoingBudget ?? 0);
  const implied = calculateMonthlyImpliedSpend(categoryExpenses, dateInput);

  return {
    budget,
    actual: implied,
    variance: implied - budget
  };
}

function getSupplierActualValue(supplierExpenseRows, dateInput, reviewMode) {
  if (reviewMode === BUDGET_REVIEW_MODE_IRREGULAR) {
    return sumExpenseAmounts(supplierExpenseRows);
  }

  return calculateMonthlyImpliedSpend(supplierExpenseRows, dateInput);
}

function buildBudgetReviewTree(rows, expenseRows, dateInput, filter, reviewMode, t = (key) => key) {
  const expenseType = reviewMode === BUDGET_REVIEW_MODE_IRREGULAR ? "irregular" : "ongoing";
  const categories = rows.map((row) => {
    const categoryKey = normalizeBudgetReviewKeyPart(row.category, "uncategorized");
    const categoryExpenses = categoryExpenseRows(expenseRows, row.category)
      .filter((expenseRow) => normalizeExpenseType(expenseRow.type) === expenseType);
    const { budget, actual, variance } = getBudgetReviewCategoryValues(row, categoryExpenses, dateInput, reviewMode);
    const percent = budgetUsagePercent(actual, budget);
    const tone = variance > 0 ? "bad" : "good";
    const supplierTotals = new Map();

    categoryExpenses.forEach((expenseRow) => {
      const supplier = getSupplierLabel(expenseRow, t);
      const current = supplierTotals.get(supplier) ?? 0;
      supplierTotals.set(supplier, current + Number(expenseRow.amount ?? 0));
    });

    const suppliers = Array.from(supplierTotals.entries())
      .map(([label]) => {
        const supplierExpenseRows = categoryExpenses.filter((expenseRow) => getSupplierLabel(expenseRow, t) === label);
        const supplierActual = getSupplierActualValue(supplierExpenseRows, dateInput, reviewMode);
        const supplierPercent = actual > 0 ? (supplierActual / actual) * 100 : 0;

        return {
          id: `${reviewMode}|category:${categoryKey}|supplier:${normalizeBudgetReviewKeyPart(label, "unlabeled")}`,
          label,
          monthlyBudget: null,
          implied: supplierActual,
          variance: null,
          percent: supplierPercent,
          tone: "good"
        };
      })
      .sort((left, right) => right.implied - left.implied || left.label.localeCompare(right.label));

    return {
      id: `${reviewMode}|category:${categoryKey}`,
      label: row.category,
      monthlyBudget: budget,
      implied: actual,
      variance,
      percent,
      barTotal: variance > 0 ? actual : budget,
      tone,
      children: suppliers
    };
  }).filter((row) => {
    if (row.monthlyBudget === 0 && row.implied === 0) {
      return false;
    }

    if (filter === ANALYSIS_BUDGET_REVIEW_FILTER_OVER) {
      return row.variance > 0;
    }

    if (filter === ANALYSIS_BUDGET_REVIEW_FILTER_WITHIN) {
      return row.variance <= 0;
    }

    return true;
  });

  return categories.sort((left, right) => (
    right.barTotal - left.barTotal
    || Math.abs(right.variance) - Math.abs(left.variance)
    || right.implied - left.implied
    || left.label.localeCompare(right.label)
  ));
}

function buildBudgetReviewTotalRow(categoryRows = [], t = (key) => key) {
  const total = categoryRows.reduce((accumulator, row) => ({
    monthlyBudget: accumulator.monthlyBudget + Number(row.monthlyBudget ?? 0),
    implied: accumulator.implied + Number(row.implied ?? 0)
  }), {
    monthlyBudget: 0,
    implied: 0
  });
  const variance = total.implied - total.monthlyBudget;

  return {
    id: "budget-review-total",
    label: t("total"),
    monthlyBudget: total.monthlyBudget,
    implied: total.implied,
    variance,
    percent: budgetUsagePercent(total.implied, total.monthlyBudget),
    tone: variance > 0 ? "bad" : "good"
  };
}

function flattenNodes(nodes, expandedIds, depth = 0) {
  return nodes.flatMap((node) => {
    const expanded = expandedIds.has(node.id);
    const currentRow = [{
      ...node,
      depth,
      expanded
    }];

    if (!node.children?.length || !expanded) {
      return currentRow;
    }

    return currentRow.concat(flattenNodes(node.children, expandedIds, depth + 1));
  });
}

function deserializeExpandedIds(storedValue) {
  try {
    const parsed = JSON.parse(storedValue);
    return new Set(Array.isArray(parsed) ? parsed.filter((value) => typeof value === "string") : []);
  } catch {
    return new Set();
  }
}

function serializeExpandedIds(value) {
  return JSON.stringify(Array.from(value ?? []));
}

function BudgetReviewCategoryMeter({ monthlyBudget, implied, variance, widthPercent }) {
  const isOverBudget = variance > 0;
  const safeBudget = Math.max(0, monthlyBudget);
  const safeImplied = Math.max(0, implied);

  if (isOverBudget) {
    const total = Math.max(safeImplied, safeBudget, 1);
    const coveredPercent = (Math.min(safeBudget, safeImplied) / total) * 100;
    const overPercent = (Math.max(0, safeImplied - safeBudget) / total) * 100;

    return (
      <ProgressMeter
        mode="segments"
        notation="cumulative"
        showTrack={false}
        widthPercent={widthPercent}
        levels={[
          { key: "covered", percent: coveredPercent, tone: "caution" },
          { key: "over", percent: coveredPercent + overPercent, tone: "bad" }
        ]}
      />
    );
  }

  const total = Math.max(safeBudget, 1);
  const spentPercent = (Math.min(safeImplied, safeBudget) / total) * 100;
  const remainingPercent = (Math.max(0, safeBudget - safeImplied) / total) * 100;

  return (
    <ProgressMeter
      mode="segments"
      notation="cumulative"
      showTrack={false}
      widthPercent={widthPercent}
      levels={[
        { key: "spent", percent: spentPercent, tone: "caution" },
        { key: "budget", percent: spentPercent + remainingPercent, tone: "good" }
      ]}
    />
  );
}

function BudgetReviewMeterCell({ hideDistribution, row, showVariance, widthPercent }) {
  return (
    <td class="budget-review-cell budget-review-cell-meter">
      {hideDistribution ? null : showVariance ? (
        <BudgetReviewCategoryMeter
          monthlyBudget={row.monthlyBudget}
          implied={row.implied}
          variance={row.variance}
          label={row.label}
          widthPercent={widthPercent}
        />
      ) : (
        <ProgressMeter
          mode="segments"
          notation="cumulative"
          showTrack={false}
          levels={[
            {
              key: "usage",
              percent: row.percent,
              tone: "good"
            }
          ]}
        />
      )}
    </td>
  );
}

function BudgetReviewLabelCell({
  labelRef,
  onToggle,
  row
}) {
  const hasChildren = Boolean(row.children?.length);

  return (
    <td class="budget-review-cell budget-review-cell-label">
      <div class="budget-review-label-wrap" style={{ "--budget-review-node-depth": row.depth }}>
        {hasChildren ? (
          <button
            class="budget-review-toggle"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggle(row.id);
            }}
          >
            {row.expanded ? "-" : "+"}
          </button>
        ) : (
          <span class="budget-review-toggle-placeholder" />
        )}
        <span class="budget-review-label-text" ref={labelRef}>{row.label}</span>
      </div>
    </td>
  );
}

function BudgetReviewRow({
  row,
  rowIndex,
  isSelected,
  labelRef,
  onSelect,
  onToggle,
  maxCategoryTotal,
  hideCategoryDistribution
}) {
  const hasChildren = Boolean(row.children?.length);
  const showVariance = row.variance !== null;
  const varianceTone = showVariance ? row.tone : null;
  const totalBarAmount = showVariance
    ? (row.variance > 0 ? row.implied : row.monthlyBudget)
    : row.implied;
  const widthPercent = maxCategoryTotal > 0 ? (totalBarAmount / maxCategoryTotal) * 100 : 0;
  const hideDistribution = hideCategoryDistribution && row.depth === 0;

  return (
    <tr
      data-row-index={rowIndex}
      class={[
        "budget-review-row",
        hasChildren ? "budget-review-row--branch" : "",
        hasChildren && row.expanded ? "budget-review-row--expanded" : "",
        isSelected ? "budget-review-row--selected" : ""
      ].filter(Boolean).join(" ")}
      onClick={() => {
        onSelect(row.id);
        if (hasChildren) {
          onToggle(row.id);
        }
      }}
    >
      <BudgetReviewLabelCell
        labelRef={labelRef}
        onToggle={onToggle}
        row={row}
      />
      <td class="budget-review-cell budget-review-cell-number">
        {row.monthlyBudget !== null ? formatCurrency(row.monthlyBudget) : ""}
      </td>
      <td class="budget-review-cell budget-review-cell-number">
        {formatCurrency(row.implied)}
      </td>
      <td class={`budget-review-cell budget-review-cell-number${varianceTone ? ` tone-${varianceTone}` : ""}`}>
        {showVariance ? formatCurrency(row.variance) : ""}
      </td>
      <td class={`budget-review-cell budget-review-cell-number${varianceTone ? ` tone-${varianceTone}` : ""}`}>
        {hideDistribution ? "" : `${Math.round(row.percent)}%`}
      </td>
      <BudgetReviewMeterCell
        hideDistribution={hideDistribution}
        row={row}
        showVariance={showVariance}
        widthPercent={widthPercent}
      />
    </tr>
  );
}

function BudgetReviewTotalRow({ row }) {
  const varianceTone = row.tone;

  return (
    <tr class="budget-review-total-row">
      <th scope="row" class="budget-review-cell budget-review-cell-label">
        <div class="budget-review-label-wrap" style={{ "--budget-review-node-depth": 0 }}>
          <span class="budget-review-toggle-placeholder" />
          <span class="budget-review-label-text">{row.label}</span>
        </div>
      </th>
      <th scope="col" class="budget-review-cell budget-review-cell-number">
        {formatCurrency(row.monthlyBudget)}
      </th>
      <th scope="col" class="budget-review-cell budget-review-cell-number">
        {formatCurrency(row.implied)}
      </th>
      <th scope="col" class={`budget-review-cell budget-review-cell-number tone-${varianceTone}`}>
        {formatCurrency(row.variance)}
      </th>
      <th scope="col" class={`budget-review-cell budget-review-cell-number tone-${varianceTone}`}>
        {`${Math.round(row.percent)}%`}
      </th>
      <th scope="col" class="budget-review-cell budget-review-cell-meter">
        <BudgetReviewCategoryMeter
          monthlyBudget={row.monthlyBudget}
          implied={row.implied}
          variance={row.variance}
          widthPercent={100}
        />
      </th>
    </tr>
  );
}

export function BudgetReview({
  rows = [],
  expenseRows = [],
  dateContext,
  filter = ANALYSIS_BUDGET_REVIEW_FILTER_ALL,
  onFilterChange,
  onNavigateToPage,
  showAltHints = false,
  t = (key) => key
}) {
  const [sortState, setSortState] = useRememberedSortState(BUDGET_REVIEW_SORT_SESSION_KEY);
  const explorerRef = useRef(null);
  const modeControlRef = useRef(null);
  const filterControlRef = useRef(null);
  const [reviewMode, setReviewMode] = usePageStateMemory(BUDGET_REVIEW_MODE_SESSION_KEY, BUDGET_REVIEW_MODE_ONGOING, {
    deserialize(storedValue) {
      return [BUDGET_REVIEW_MODE_ONGOING, BUDGET_REVIEW_MODE_IRREGULAR].includes(storedValue)
        ? storedValue
        : BUDGET_REVIEW_MODE_ONGOING;
    },
    serialize(value) {
      return [BUDGET_REVIEW_MODE_ONGOING, BUDGET_REVIEW_MODE_IRREGULAR].includes(value)
        ? value
        : BUDGET_REVIEW_MODE_ONGOING;
    }
  });
  const [expandedIds, setExpandedIds] = usePageStateMemory(BUDGET_REVIEW_EXPANDED_SESSION_KEY, new Set(), {
    deserialize: deserializeExpandedIds,
    serialize: serializeExpandedIds
  });
  const [selectedRowId, setSelectedRowId] = usePageStateMemory(BUDGET_REVIEW_SELECTION_SESSION_KEY, null, {
    deserialize(storedValue) {
      try {
        const parsed = JSON.parse(storedValue);

        if (parsed?.mode !== reviewMode) {
          return null;
        }

        return parsed?.rowId ? String(parsed.rowId) : null;
      } catch {
        return null;
      }
    },
    serialize(value) {
      return JSON.stringify({
        mode: reviewMode,
        rowId: value ? String(value) : null
      });
    }
  });
  const previousReviewModeRef = useRef(reviewMode);
  const filterValue = [
    ANALYSIS_BUDGET_REVIEW_FILTER_ALL,
    ANALYSIS_BUDGET_REVIEW_FILTER_OVER,
    ANALYSIS_BUDGET_REVIEW_FILTER_WITHIN
  ].includes(filter)
    ? filter
    : ANALYSIS_BUDGET_REVIEW_FILTER_ALL;
  const handleFilterChange = onFilterChange ?? (() => {});
  const filterOptions = useMemo(() => [
    { value: ANALYSIS_BUDGET_REVIEW_FILTER_ALL, label: t("all") },
    { value: ANALYSIS_BUDGET_REVIEW_FILTER_OVER, label: t("overBudget") },
    { value: ANALYSIS_BUDGET_REVIEW_FILTER_WITHIN, label: t("withinBudget") }
  ], [t]);
  const reviewModeOptions = useMemo(() => [
    { value: BUDGET_REVIEW_MODE_ONGOING, label: t("ongoing") },
    { value: BUDGET_REVIEW_MODE_IRREGULAR, label: t("irregular") }
  ], [t]);
  const tree = useMemo(
    () => buildBudgetReviewTree(rows, expenseRows, dateContext, filterValue, reviewMode, t),
    [dateContext, expenseRows, filterValue, reviewMode, rows, t]
  );
  const sortedTree = useMemo(() => {
    const nextTree = tree.slice();

    if (!sortState) {
      return nextTree.sort((left, right) => (
        right.barTotal - left.barTotal
        || Math.abs(right.variance) - Math.abs(left.variance)
        || left.label.localeCompare(right.label)
      ));
    }

    if (sortState.key === BUDGET_REVIEW_SORT_LABEL) {
      nextTree.sort((left, right) => left.label.localeCompare(right.label));
      return sortState.direction === "asc" ? nextTree : nextTree.reverse();
    }

    if (sortState.key === BUDGET_REVIEW_SORT_BUDGET) {
      nextTree.sort((left, right) => left.monthlyBudget - right.monthlyBudget || left.label.localeCompare(right.label));
      return sortState.direction === "asc" ? nextTree : nextTree.reverse();
    }

    if (sortState.key === BUDGET_REVIEW_SORT_IMPLIED) {
      nextTree.sort((left, right) => left.implied - right.implied || left.label.localeCompare(right.label));
      return sortState.direction === "asc" ? nextTree : nextTree.reverse();
    }

    if (sortState.key === BUDGET_REVIEW_SORT_PERCENT) {
      nextTree.sort((left, right) => left.percent - right.percent || left.label.localeCompare(right.label));
      return sortState.direction === "asc" ? nextTree : nextTree.reverse();
    }

    if (sortState.key === BUDGET_REVIEW_SORT_METER) {
      nextTree.sort((left, right) => left.barTotal - right.barTotal || left.label.localeCompare(right.label));
      return sortState.direction === "asc" ? nextTree : nextTree.reverse();
    }

    nextTree.sort((left, right) => left.variance - right.variance || left.label.localeCompare(right.label));
    return sortState.direction === "asc" ? nextTree : nextTree.reverse();
  }, [sortState, tree]);
  const visibleRows = useMemo(() => flattenNodes(sortedTree, expandedIds), [expandedIds, sortedTree]);
  const totalRow = useMemo(
    () => buildBudgetReviewTotalRow(sortedTree, t),
    [sortedTree, t]
  );
  const maxCategoryTotal = useMemo(() => {
    const categoryTotals = sortedTree.map((row) => (
      row.variance > 0 ? row.implied : row.monthlyBudget
    ));

    return categoryTotals.reduce((currentMax, value) => Math.max(currentMax, value), 0);
  }, [sortedTree]);
  const toolbarActionsForRow = useCallback((row) => {
    if (!row) {
      return [];
    }

    if (row.depth === 0) {
      return [
        {
          key: "show-in-expenses",
          label: t("expenses"),
          onSelect: () => {
            setExpensesSearchQuery(row.label);
            onNavigateToPage?.("expenses");
          }
        },
        {
          key: "show-in-budget",
          label: t("budget"),
          onSelect: () => {
            setBudgetSearchQuery(row.label);
            onNavigateToPage?.("budget");
          }
        },
        {
          key: "show-in-chart",
          label: t("chart"),
          onSelect: () => {
            setAnalysisChartCategory(row.label);
            onNavigateToPage?.("analysis", "charts");
          }
        }
      ];
    }

    if (row.depth === 1) {
      return [
        {
          key: "show-in-expenses",
          label: t("expenses"),
          onSelect: () => {
            setExpensesSearchQuery(row.label);
            onNavigateToPage?.("expenses");
          }
        },
        {
          key: "show-in-chart",
          label: t("chart"),
          onSelect: () => {
            setAnalysisChartSupplier(row.label);
            onNavigateToPage?.("analysis", "charts");
          }
        }
      ];
    }

    return [];
  }, [onNavigateToPage, t]);

  useEffect(() => {
    focusExplorer();
  }, []);

  useEffect(() => {
    if (previousReviewModeRef.current === reviewMode) {
      return;
    }

    previousReviewModeRef.current = reviewMode;
    setSelectedRowId(null);
  }, [reviewMode, setSelectedRowId]);

  function focusExplorer() {
    explorerRef.current?.focus({ preventScroll: true });
  }

  function toggleRow(rowId) {
    setExpandedIds((current) => {
      const nextExpanded = new Set(current);

      if (nextExpanded.has(rowId)) {
        nextExpanded.delete(rowId);
      } else {
        nextExpanded.add(rowId);
      }

      return nextExpanded;
    });
  }

  const pageBindings = [
    ...createSectionNavigationBindings({
      options: reviewModeOptions,
      value: reviewMode,
      onChange: setReviewMode,
      allowInInput: true
    }),
    onKey("Alt+E", () => {
      const activeButton = modeControlRef.current?.querySelector(".segmented-control-option.active");
      const fallbackButton = modeControlRef.current?.querySelector(".segmented-control-option");
      (activeButton ?? fallbackButton)?.focus();
    }, { allowInInput: true }),
    onKey("Alt+W", () => {
      focusExplorer();
    }, { allowInInput: true })
  ];
  const hasEscapeAction = hasPageEscapeResetAction({
    sortState,
    extraResetAvailable: expandedIds.size > 0
  });
  useEscapeStack(() => handlePageEscapeReset({
    sortState,
    setSortState,
    extraResetAvailable: expandedIds.size > 0,
    onExtraReset: () => {
      setExpandedIds(new Set());
    },
    onResetApplied: focusExplorer
  }), hasEscapeAction, -100);

  useShortcutBindings(window, pageBindings);
  const hideCategoryDistribution = expandedIds.size > 0;
  const headerCells = useMemo(() => ([
    {
      key: BUDGET_REVIEW_SORT_LABEL,
      label: t("group"),
      align: "text",
      sortable: true
    },
    {
      key: BUDGET_REVIEW_SORT_BUDGET,
      label: t("budget"),
      align: "number",
      sortable: true
    },
    {
      key: BUDGET_REVIEW_SORT_IMPLIED,
      label: reviewMode === BUDGET_REVIEW_MODE_IRREGULAR ? t("spent") : t("implied"),
      align: "number",
      sortable: true
    },
    {
      key: BUDGET_REVIEW_SORT_VARIANCE,
      label: t("variance"),
      align: "number",
      sortable: true
    },
    {
      key: BUDGET_REVIEW_SORT_PERCENT,
      label: t("usage"),
      align: "number",
      sortable: true
    },
    {
      key: BUDGET_REVIEW_SORT_METER,
      label: t("meter"),
      align: "text",
      sortable: true
    }
  ]), [reviewMode, t]);

  return (
    <Panel
      className="budget-review-panel fill-area"
      toolbar={(
        <ControlBar
          boxed={false}
          start={(
            <AltHintTarget label="E" visible={showAltHints} targetRef={modeControlRef}>
              <SegmentedControl
                options={reviewModeOptions}
                value={reviewMode}
                onChange={setReviewMode}
              />
            </AltHintTarget>
          )}
          middle={(
            <div class="budget-review-legend">
              <span class="budget-review-legend-item">
                <span class="budget-review-legend-swatch tone-caution" />
                {reviewMode === BUDGET_REVIEW_MODE_IRREGULAR ? t("spent") : t("implied")}
              </span>
              <span class="budget-review-legend-item">
                <span class="budget-review-legend-swatch tone-good" />
                {t("remaining")}
              </span>
              <span class="budget-review-legend-item">
                <span class="budget-review-legend-swatch tone-bad" />
                {t("overrun")}
              </span>
            </div>
          )}
          end={(
            <div ref={filterControlRef}>
                <SegmentedControl
                  options={filterOptions}
                  value={filterValue}
                  onChange={handleFilterChange}
                />
            </div>
          )}
        />
      )}
    >
      <HierarchicalReviewSurface
        rows={visibleRows}
        selectedRowId={selectedRowId}
        onSelectedRowIdChange={setSelectedRowId}
        onToggleRow={toggleRow}
        headerCells={headerCells}
        renderHeaderSummaryRow={() => (
          <BudgetReviewTotalRow row={totalRow} />
        )}
        sortState={sortState}
        onSortChange={setSortState}
        renderRow={({ row, rowIndex, isSelected, labelRef, onSelect, onToggle }) => (
          <BudgetReviewRow
            key={row.id}
            rowIndex={rowIndex}
            row={row}
            isSelected={isSelected}
            labelRef={labelRef}
            maxCategoryTotal={maxCategoryTotal}
            hideCategoryDistribution={hideCategoryDistribution}
            onSelect={onSelect}
            onToggle={onToggle}
          />
        )}
        emptyContent={t("noBudgetRowsToShow")}
        baseClassName="budget-review"
        dataRole="budget-review-grid"
        surfaceRef={explorerRef}
        showAltHints={showAltHints}
        toolbarPlacement="inline-end"
        toolbarOffset={24}
        getToolbarActions={toolbarActionsForRow}
      />
    </Panel>
  );
}
